import {
  FormatType,
  getOtherColor,
  StackedBarChartHorizontal,
  type StackedBarData,
} from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import type { BreakdownSingleDataSet } from '../DataProvider/types';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { useChart } from './ChartProvider';
import { SharableChart } from './chart-menu/plot-share/SharableChart';
import { isOtherSegment } from './utils/group-and-filter-data';

export const StackedBar = ({
  data,
  metric,
  hideLegend = false,
}: {
  data: BreakdownSingleDataSet['data'];
  metric?: MetricValues;
  hideLegend?: boolean;
}) => {
  const { item } = useChart();
  const colorPalette = item?.plot?.colorPalette;
  const { code } = useCurrencyPreference();

  const stackedBarChartData: StackedBarData[] = data.map((entity) => {
    const segments = (entity.breakdown ?? []).reduce<Record<string, number>>(
      (acc, b) => ({ ...acc, [b.dim]: b.value ?? 0 }),
      {}
    );

    return { label: entity.dim, segments };
  });

  const segmentOrder = (() => {
    const seen = new Set<string>();
    const nonOther: string[] = [];
    const other: string[] = [];
    data.forEach((entity) => {
      (entity.breakdown ?? []).forEach((b) => {
        if (seen.has(b.dim)) return;
        seen.add(b.dim);
        if (isOtherSegment({ dim: b.dim, id: b.id })) other.push(b.dim);
        else nonOther.push(b.dim);
      });
    });
    return [...nonOther, ...other];
  })();

  const format = (() => {
    const plotFormat = item?.plot?.format;
    if (plotFormat !== undefined) {
      return plotFormat;
    }
    switch (metric) {
      case 'headcount':
        return FormatType.INTEGER;

      case 'tenure':
        return FormatType.YEAR;

      case 'base_salary':
      case 'total_compensation':
      case 'contractor_pay_rate':
      case 'total_spend_salary':
      case 'total_spend_compensation':
        return FormatType.CURRENCY;

      default:
        return FormatType.INTEGER;
    }
  })();

  return (
    <SharableChart
      Component={StackedBarChartHorizontal}
      data={stackedBarChartData}
      hideLegend={hideLegend}
      colorOverride={{ Other: getOtherColor() }}
      colorPalette={colorPalette}
      format={format}
      currencyCode={code}
      segmentOrder={segmentOrder}
    />
  );
};
